import { FC, useState, useMemo } from 'react';
import { Accordion, Col, Row, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { Location } from '../../../utils/interface';

interface LocationsAccordianProps {
    locations: Location[];
}

const LocationsAccordian: FC<LocationsAccordianProps> = (props) => {
    const { locations } = props;
    const [searchQuery, setSearchQuery] = useState('');

    const filteredLocations = useMemo(() => {
        if (!searchQuery.trim()) return locations;
        const q = searchQuery.toLowerCase().trim();
        return locations.filter((loc) =>
            (loc.display_name && loc.display_name.toLowerCase().includes(q)) ||
            (loc.address && loc.address.toLowerCase().includes(q)) ||
            (loc.city_name && loc.city_name.toLowerCase().includes(q)) ||
            (loc.state_name && loc.state_name.toLowerCase().includes(q)) ||
            (loc.phone && loc.phone.toLowerCase().includes(q)) ||
            (loc.reply_to_email && loc.reply_to_email.toLowerCase().includes(q))
        );
    }, [locations, searchQuery]);

    return (
        <div className="locations-accordian-wrapper">
            {locations.length > 3 && (
                <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="position-relative flex-grow-1" style={{ maxWidth: '340px' }}>
                        <Form.Control
                            type="text"
                            placeholder="Search locations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: '32px', height: '40px', fontSize: '14px' }}
                        />
                        <span
                            className="position-absolute text-muted"
                            style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </span>
                    </div>
                    <span className="text-muted" style={{ fontSize: '14px' }}>
                        Showing {filteredLocations.length} of {locations.length} locations
                    </span>
                </div>
            )}
            <div
                className="locations-scroll-container"
                style={{
                    maxHeight: '62vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '6px',
                }}
            >
                {filteredLocations.length === 0 ? (
                    <p className="text-center text-muted py-4">No locations match "{searchQuery}".</p>
                ) : (
                    <Accordion defaultActiveKey="0">
                        {filteredLocations.map((v, k) => {
                            return (
                                <Accordion.Item eventKey={k.toString()} key={v.location_id || v.id || k}>
                                    <Accordion.Header>{v?.display_name ? `${v.display_name} (Location ${k + 1})` : `Location ${k + 1}`}</Accordion.Header>
                                    <Accordion.Body>
                                        <Row className="align-items-center">
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Location Logo</span>
                                            <div className='text-btm mt-1'>
                                                {v?.logoUrl ? (
                                                    <img
                                                        src={v.logoUrl}
                                                        alt={v.display_name || "Location Logo"}
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: '1px solid #ddd',
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#f8f9fa',
                                                            border: '1px dashed #ced4da',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#adb5bd',
                                                            fontSize: '20px',
                                                        }}
                                                        title="No Location Logo"
                                                    >
                                                        <FontAwesomeIcon icon={faImage} />
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Location Name</span>
                                            <span className='text-btm'>{v?.display_name || 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Phone</span>
                                            <span className='text-btm'>{v?.phone || 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Reply-to Email</span>
                                            <span className='text-btm'>{v?.reply_to_email || 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>From Email Name</span>
                                            <span className='text-btm'>{v?.from_name || 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Status</span>
                                            <span className='text-btm'>{v?.is_active !== false ? 'Active' : 'Inactive'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Timezone</span>
                                            <span className='text-btm'>{v?.location_timezone || 'UTC'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Address</span>
                                            <span className='text-btm'>{v?.address ?? 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Country</span>
                                            <span className='text-btm'>{v?.country_name ?? 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>State</span>
                                            <span className='text-btm'>{v?.state_name ?? 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                 <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>City</span>
                                            <span className='text-btm'>{v?.city_name ?? v?.city ?? 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                                  <Col xxl={4} xl={6} lg={6} md={6} sm={6} xs={12}>
                                    <ul>
                                        <li>
                                            <span className='text-tp'>Zip Code</span>
                                            <span className='text-btm'>{v?.zipcode ?? 'N/A'}</span>
                                        </li>
                                    </ul>
                                </Col>
                            </Row>
                        </Accordion.Body>
                    </Accordion.Item>
                );
                        })}
                    </Accordion>
                )}
            </div>
        </div>
    );
};

export default LocationsAccordian;
